<!--
  The day/week scheduler grid (masterplan sprint 13, directive section 7).

  This is a broadcast schedule, not a calendar. The differences are deliberate:

  - a block's colour carries readiness, not category, because "will this air" is the
    question a programmer has and a calendar cannot answer it;
  - an exact-time item is marked with a shape as well as a colour, so the one class of
    item the scheduler may never slide is identifiable without colour vision;
  - the current time is a line across every day, because a programmer's first question
    on opening this page is what is on now;
  - a slot that has already aired is visually settled rather than highlighted; nothing
    can be done about it any more.

  Moving a block is pointer-driven with a keyboard equivalent on every block, because an
  operator fixing a schedule at 02:00 may be on a laptop trackpad and may be on a phone.
-->
<script setup lang="ts">
import { computed, ref } from 'vue';
import { getUiLocale } from '../clock.js';
import type { ScheduleGridLabels } from '../labels.js';
import {
  HOUR_HEIGHT_PX,
  dayBoundsFrom,
  layoutBlocks,
  nowPosition,
  snapDrop,
} from '../schedule-layout.js';

export type ReadinessState = 'READY' | 'PENDING' | 'BLOCKED' | 'AIRED' | 'NOT_APPLICABLE';

export interface ScheduleBlock {
  id: string;
  title: string;
  subtitle?: string | null;
  /** ISO timestamps. */
  startAt: string;
  endAt: string;
  kind: string;
  status: string;
  isExactTime?: boolean;
  isRerun?: boolean;
  readiness?: ReadinessState;
  readinessProblem?: string | null;
  /** Set when the slot overlaps another commitment. */
  conflict?: boolean;
  /** False for a slot inside the pre-air freeze window without override authority. */
  movable?: boolean;
}

const props = withDefaults(
  defineProps<{
    /** The first day shown, at local midnight. */
    from: Date;
    /** 1 for a day view, 7 for a week. */
    days?: number;
    blocks: ScheduleBlock[];
    /** Station-local IANA zone, so the grid matches the broadcast clock, not the laptop. */
    timezone?: string;
    /** Minutes a drag snaps to. Five, because broadcast schedules are not to the second. */
    snapMinutes?: number;
    now?: Date;
    loading?: boolean;
    /** Translated words for markers and spoken labels; anything missing is English. */
    labels?: ScheduleGridLabels;
  }>(),
  { days: 7, snapMinutes: 5, timezone: 'Europe/Amsterdam' },
);

const words = computed(() => ({
  to: 'to',
  now: 'Now',
  exactTime: 'Exact time',
  rerun: 'Rerun',
  conflict: 'Overlaps another slot',
  locked: 'Inside the freeze window',
  ...props.labels,
}));

const emit = defineEmits<{
  (event: 'select', block: ScheduleBlock): void;
  (event: 'move', payload: { block: ScheduleBlock; startAt: Date; endAt: Date }): void;
}>();

/** A drag in progress. Kept local so a failed save leaves the grid where the data says. */
const dragging = ref<{ id: string; offsetMinutes: number } | null>(null);

const dayList = computed(() => dayBoundsFrom(props.from, props.days, props.timezone));

/**
 * The hour rows, from the longest day shown. A clock-change day is 23 or 25 hours long,
 * and drawing 24 rows on it puts every block after the change on the wrong line.
 */
const hours = computed(() => {
  const longest = Math.max(
    24,
    ...dayList.value.map((day) =>
      Math.round((day.end.getTime() - day.start.getTime()) / 3_600_000),
    ),
  );
  return Array.from({ length: longest }, (_, hour) => hour);
});

/** The clock hour printed on a gutter row, in the station's zone. */
function hourLabel(hour: number): string {
  const first = dayList.value[0];
  if (!first) return `${String(hour).padStart(2, '0')}:00`;
  return new Intl.DateTimeFormat(getUiLocale(), {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: props.timezone,
  }).format(new Date(first.start.getTime() + hour * 3_600_000));
}

interface PositionedBlock extends ScheduleBlock {
  dayIndex: number;
  topPx: number;
  heightPx: number;
  /** True when the slot started on the previous day and continues into this one. */
  continued: boolean;
  column: number;
  columns: number;
}

const positioned = computed<PositionedBlock[]>(() => {
  const byId = new Map(props.blocks.map((block) => [block.id, block]));
  return layoutBlocks(props.blocks, dayList.value).flatMap((placed) => {
    const block = byId.get(placed.id);
    return block ? [{ ...block, ...placed }] : [];
  });
});

const nowMarker = computed(() => nowPosition(props.now ?? new Date(), dayList.value));

function dayLabel(date: Date): string {
  return new Intl.DateTimeFormat(getUiLocale(), {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: props.timezone,
  }).format(date);
}

function timeLabel(iso: string): string {
  return new Intl.DateTimeFormat(getUiLocale(), {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: props.timezone,
  }).format(new Date(iso));
}

function blockLabel(block: PositionedBlock): string {
  const parts = [
    `${block.title}, ${timeLabel(block.startAt)} ${words.value.to} ${timeLabel(block.endAt)}`,
    block.isExactTime ? words.value.exactTime : null,
    block.isRerun ? words.value.rerun : null,
    block.conflict ? words.value.conflict : null,
    block.readinessProblem,
  ].filter(Boolean);
  return parts.join(', ');
}

function readinessTone(block: ScheduleBlock): string {
  switch (block.readiness) {
    case 'READY':
      return 'ready';
    case 'BLOCKED':
      return 'blocked';
    case 'PENDING':
      return 'pending';
    case 'AIRED':
      return 'aired';
    default:
      return 'neutral';
  }
}

function onDragStart(event: DragEvent, block: PositionedBlock): void {
  if (block.movable === false) {
    event.preventDefault();
    return;
  }

  // Where inside the block the pointer grabbed it, so the block does not jump to the
  // cursor on drop.
  const target = event.currentTarget as HTMLElement;
  const offsetPx = event.clientY - target.getBoundingClientRect().top;
  dragging.value = { id: block.id, offsetMinutes: (offsetPx / HOUR_HEIGHT_PX) * 60 };
  event.dataTransfer?.setData('text/plain', block.id);
}

function onDrop(event: DragEvent, dayIndex: number): void {
  event.preventDefault();
  const drag = dragging.value;
  dragging.value = null;
  if (!drag) return;

  const block = props.blocks.find((candidate) => candidate.id === drag.id);
  if (!block) return;

  const column = event.currentTarget as HTMLElement;
  const startAt = snapDrop({
    day: dayList.value[dayIndex]!,
    offsetPx: event.clientY - column.getBoundingClientRect().top,
    grabOffsetMinutes: drag.offsetMinutes,
    snapMinutes: props.snapMinutes,
  });
  const duration = new Date(block.endAt).getTime() - new Date(block.startAt).getTime();

  // A drop that changes nothing is not a move. Emitting it would produce an audit entry
  // and a plan recompile for a slot the operator only nudged.
  if (startAt.getTime() === new Date(block.startAt).getTime()) return;

  emit('move', { block, startAt, endAt: new Date(startAt.getTime() + duration) });
}

/**
 * The same wall-clock time a day earlier or later, in the station's zone.
 *
 * Adding 24 hours would move a 20:00 show to 19:00 or 21:00 across a clock change, which
 * is precisely the class of bug the whole scheduler is meant not to have.
 */
function sameTimeOnNeighbouringDay(start: Date, dayStep: number, minutes: number): Date {
  const neighbour = dayBoundsFrom(
    new Date(start.getTime() + dayStep * 86_400_000),
    1,
    props.timezone,
  )[0]!;
  const current = dayBoundsFrom(start, 1, props.timezone)[0]!;
  const intoDay = start.getTime() - current.start.getTime();
  return new Date(neighbour.start.getTime() + intoDay + minutes * 60_000);
}

/** Keyboard nudge, so every move a pointer can make is reachable without one. */
function onKeydown(event: KeyboardEvent, block: ScheduleBlock): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    emit('select', block);
    return;
  }

  if (block.movable === false) return;

  const step = event.key === 'ArrowUp' ? -1 : event.key === 'ArrowDown' ? 1 : 0;
  const dayStep = event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0;
  if (step === 0 && dayStep === 0) return;

  event.preventDefault();
  const minutes = step * (event.shiftKey ? 60 : props.snapMinutes);
  const start = new Date(block.startAt);
  const duration = new Date(block.endAt).getTime() - start.getTime();
  // A day step moves to the same wall-clock time on the neighbouring day, which is what
  // "move this show to Tuesday" means. Adding 24 hours would shift it across a DST change.
  const startAt =
    dayStep === 0
      ? new Date(start.getTime() + minutes * 60_000)
      : sameTimeOnNeighbouringDay(start, dayStep, minutes);

  emit('move', { block, startAt, endAt: new Date(startAt.getTime() + duration) });
}
</script>

<template>
  <div class="shio-grid" :class="{ 'shio-grid--loading': loading }">
    <div class="shio-grid__header" :style="{ '--shio-grid-days': days }">
      <div class="shio-grid__gutter-head" aria-hidden="true"></div>
      <div v-for="day in dayList" :key="day.start.toISOString()" class="shio-grid__day-head">
        {{ dayLabel(day.start) }}
      </div>
    </div>

    <div class="shio-grid__body" :style="{ '--shio-grid-days': days }">
      <div class="shio-grid__gutter" aria-hidden="true">
        <div v-for="hour in hours" :key="hour" class="shio-grid__hour-label">
          {{ hourLabel(hour) }}
        </div>
      </div>

      <div
        v-for="(day, dayIndex) in dayList"
        :key="day.start.toISOString()"
        class="shio-grid__column"
        @dragover.prevent
        @drop="onDrop($event, dayIndex)"
      >
        <div v-for="hour in hours" :key="hour" class="shio-grid__hour"></div>

        <div
          v-if="nowMarker && nowMarker.dayIndex === dayIndex"
          class="shio-grid__now"
          :style="{ top: `${nowMarker.topPx}px` }"
          role="separator"
          :aria-label="words.now"
        ></div>

        <button
          v-for="block in positioned.filter((candidate) => candidate.dayIndex === dayIndex)"
          :key="`${block.id}-${block.dayIndex}`"
          type="button"
          class="shio-grid__block"
          :class="[
            `shio-grid__block--${readinessTone(block)}`,
            {
              'shio-grid__block--exact': block.isExactTime,
              'shio-grid__block--conflict': block.conflict,
              'shio-grid__block--continued': block.continued,
              'shio-grid__block--locked': block.movable === false,
              // Under about 40 minutes there is only room for the title. The time is
              // dropped before the title, because the title identifies the slot and the
              // row position already says roughly when it airs.
              // A block under about an hour has room for one line of title, not two.
              'shio-grid__block--tight': block.heightPx < 72,
              'shio-grid__block--compact': block.heightPx < 40,
            },
          ]"
          :style="{
            top: `${block.topPx}px`,
            height: `${block.heightPx}px`,
            left: `calc(${(block.column / block.columns) * 100}% + 2px)`,
            width: `calc(${100 / block.columns}% - 4px)`,
          }"
          :draggable="block.movable !== false"
          :aria-label="blockLabel(block)"
          @dragstart="onDragStart($event, block)"
          @dragend="dragging = null"
          @click="emit('select', block)"
          @keydown="onKeydown($event, block)"
        >
          <span class="shio-grid__block-time shio-tabular">{{ timeLabel(block.startAt) }}</span>
          <span class="shio-grid__block-title">{{ block.title }}</span>
          <span v-if="block.subtitle && block.heightPx >= 72" class="shio-grid__block-sub">
            {{ block.subtitle }}
          </span>

          <span class="shio-grid__markers">
            <!-- Shape, not only colour: this is the one class of item that may never slide. -->
            <span v-if="block.isExactTime" class="shio-grid__marker" :title="words.exactTime">
              ▣
            </span>
            <span v-if="block.isRerun" class="shio-grid__marker" :title="words.rerun">↻</span>
            <span v-if="block.conflict" class="shio-grid__marker" :title="words.conflict">⚠</span>
            <span v-if="block.movable === false" class="shio-grid__marker" :title="words.locked">
              🔒
            </span>
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.shio-grid {
  display: grid;
  gap: 0;
  border: 1px solid hsl(var(--shio-border));
  border-radius: var(--shio-radius-md);
  background: hsl(var(--shio-surface-base));
  overflow: hidden;
}

.shio-grid--loading {
  opacity: 0.6;
}

.shio-grid__header,
.shio-grid__body {
  display: grid;
  grid-template-columns: 4.5rem repeat(var(--shio-grid-days), minmax(7rem, 1fr));
}

.shio-grid__header {
  position: sticky;
  top: 0;
  z-index: 2;
  background: hsl(var(--shio-surface-elevated));
  border-bottom: 1px solid hsl(var(--shio-border));
}

.shio-grid__day-head {
  padding: var(--shio-space-2) var(--shio-space-3);
  font-family: var(--shio-font-body);
  font-size: var(--shio-type-body-sm-size);
  font-weight: 600;
  color: hsl(var(--shio-text-primary));
  border-left: 1px solid hsl(var(--shio-border));
}

.shio-grid__body {
  position: relative;
  max-height: 70vh;
  overflow-y: auto;
}

.shio-grid__gutter {
  border-right: 1px solid hsl(var(--shio-border));
}

.shio-grid__hour-label {
  height: 56px;
  padding-right: var(--shio-space-2);
  text-align: right;
  font-size: var(--shio-type-meta-size);
  font-variant-numeric: tabular-nums;
  color: hsl(var(--shio-text-muted));
  transform: translateY(-0.5em);
}

.shio-grid__column {
  position: relative;
  border-left: 1px solid hsl(var(--shio-border));
}

.shio-grid__hour {
  height: 56px;
  border-bottom: 1px solid hsl(var(--shio-border) / 0.5);
}

.shio-grid__now {
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background: hsl(var(--shio-accent-primary));
  box-shadow: 0 0 8px hsl(var(--shio-accent-primary) / 0.6);
  z-index: 1;
}

.shio-grid__block {
  position: absolute;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 2px var(--shio-space-2);
  text-align: left;
  overflow: hidden;
  border: 1px solid hsl(var(--shio-border));
  border-left-width: 3px;
  border-radius: var(--shio-radius-sm);
  background: hsl(var(--shio-surface-elevated));
  color: hsl(var(--shio-text-primary));
  font-family: var(--shio-font-body);
  cursor: grab;
}

.shio-grid__block:focus-visible {
  outline: 2px solid hsl(var(--shio-accent-cyan));
  outline-offset: 1px;
  z-index: 3;
}

.shio-grid__block--ready {
  border-left-color: hsl(var(--shio-status-success));
}
.shio-grid__block--pending {
  border-left-color: hsl(var(--shio-status-warning));
}
.shio-grid__block--blocked {
  border-left-color: hsl(var(--shio-status-danger));
  background: hsl(var(--shio-status-danger) / 0.08);
}
.shio-grid__block--aired {
  border-left-color: hsl(var(--shio-text-muted));
  opacity: 0.7;
}
.shio-grid__block--neutral {
  border-left-color: hsl(hsl(var(--shio-border)));
}

.shio-grid__block--exact {
  border-style: solid;
  border-top: 2px solid hsl(var(--shio-accent-cyan));
}

.shio-grid__block--conflict {
  outline: 1px dashed hsl(var(--shio-status-danger-strong));
  outline-offset: -3px;
}

.shio-grid__block--continued {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}

.shio-grid__block--locked {
  cursor: not-allowed;
}

.shio-grid__block-time {
  font-size: var(--shio-type-meta-size);
  color: hsl(var(--shio-text-muted));
}

.shio-grid__block-title {
  font-size: var(--shio-type-body-sm-size);
  font-weight: 600;
  line-height: 1.2;
  /* Ends with an ellipsis rather than being cut through a word. A half-word is worse
     than a shortened one: it reads as a rendering fault. */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}

/* One line of title on a block too short for two. An ellipsis is honest; a line cut
   through its own height reads as a rendering fault. */
.shio-grid__block--tight .shio-grid__block-title {
  -webkit-line-clamp: 1;
  line-clamp: 1;
}

.shio-grid__block--compact .shio-grid__block-time {
  display: none;
}

.shio-grid__block-sub {
  font-size: var(--shio-type-meta-size);
  color: hsl(var(--shio-text-muted));
}

.shio-grid__markers {
  position: absolute;
  top: 2px;
  right: 4px;
  display: flex;
  gap: 2px;
  font-size: var(--shio-type-meta-size);
}

/* On a narrow screen the week becomes horizontally scrollable rather than dropping
   days. A programmer looking at Thursday on a phone still needs Thursday. */
@media (max-width: 720px) {
  .shio-grid__body,
  .shio-grid__header {
    grid-template-columns: 3rem repeat(var(--shio-grid-days), minmax(6rem, 1fr));
  }
}

@media (prefers-reduced-motion: reduce) {
  .shio-grid__block {
    transition: none;
  }
}
</style>
